import { useMemo, useState, type ReactNode } from "react";
import { Building2, CalendarClock, Layers, Repeat, Search, SlidersHorizontal, SquareCheck, UserRound, X, type LucideIcon } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Combobox, type TComboSelectedValue } from "@/modern/components/ui/combobox";
import { Input } from "@/modern/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";
import { useBoardUsers } from "@/react/hooks/useBoardUsers";
import type { CheckGridOverviewItem, CheckGridRecurrence, CheckGridSubjectType } from "./types";
import { recurrenceLabel } from "./utils/display";
import { CREATED_OPTIONS, EMPTY_FILTERS, countActiveFilters, entityKey, type CheckgridFilters } from "./utils/filters";
import { subjectTypeFromOverview } from "./utils/hostEntity";

const ENTITY_TYPE_OPTIONS: { value: CheckGridSubjectType; label: string; icon: LucideIcon }[] = [
  { value: "task", label: "Task", icon: SquareCheck },
  { value: "client", label: "Client", icon: Building2 },
  { value: "resource", label: "Resource", icon: UserRound }
];
const RECURRENCE_OPTIONS: CheckGridRecurrence[] = ["none", "daily", "weekly", "monthly"];

type Props = {
  /** Every checklist before filtering — option lists are built from these. */
  grids: CheckGridOverviewItem[];
  filters: CheckgridFilters;
  onChange: (filters: CheckgridFilters) => void;
  matchCount: number;
  /** Rows the list API reports for the current server-side filters (loaded pages can be fewer). */
  totalCount: number;
  userName: (id: number) => string | undefined;
};

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function FilterChip({ selected, onClick, leading, children }: { selected: boolean; onClick: () => void; leading?: ReactNode; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] leading-none transition-all duration-150 active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected
          ? "border-primary bg-primary/20 font-medium text-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
          : "border-border bg-muted/40 text-foreground/80 hover:border-foreground/30 hover:bg-muted hover:text-foreground"
      )}
    >
      {leading}
      {children}
    </button>
  );
}

function FilterField({ label, icon: Icon, children }: { label: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="space-y-2.5">
      <h3 className="flex items-center gap-1.5 text-[12.5px] font-medium text-foreground/90">
        <Icon className="size-3.5 text-primary" aria-hidden />
        {label}
      </h3>
      {children}
    </div>
  );
}

export function CheckgridsFilters({ grids, filters, onChange, matchCount, totalCount, userName }: Props) {
  const total = Math.max(totalCount, grids.length);
  const [open, setOpen] = useState(false);
  const active = countActiveFilters(filters);
  const set = <K extends keyof CheckgridFilters>(key: K, value: CheckgridFilters[K]) => onChange({ ...filters, [key]: value });

  const { all: allBoardUsers, myBoardUserId: getMyBoardUserId } = useBoardUsers();
  // Every active board member, plus anyone already assigned (even if deactivated) so existing checklists stay filterable.
  const assigneeOptions = useMemo(() => {
    const ids = new Set(allBoardUsers().filter((member) => !member.archived && !member.isDeactivated).map((member) => member.id));
    for (const grid of grids) grid.assigned_boarduser_ids?.forEach((id) => ids.add(id));
    const myBoardUserId = getMyBoardUserId();
    return [...ids]
      .map((id) => ({ value: id, label: `${userName(id) ?? `User ${id}`}${id === myBoardUserId ? " (you)" : ""}` }))
      .sort((a, b) => Number(b.value === myBoardUserId) - Number(a.value === myBoardUserId) || a.label.localeCompare(b.label));
  }, [allBoardUsers, getMyBoardUserId, grids, userName]);

  const entityOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const grid of grids) {
      const key = entityKey(grid);
      const type = subjectTypeFromOverview(grid);
      if (!key || (filters.entityTypes.length && !filters.entityTypes.includes(type))) continue;
      byKey.set(key, `${ENTITY_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type} · ${grid.entity_name}`);
    }
    return [...byKey.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [grids, filters.entityTypes]);

  // HQ `entity_type` is a single value — chips are exclusive (same as Created).
  const selectEntityType = (type: CheckGridSubjectType) => {
    const entityTypes = filters.entityTypes.includes(type) ? [] : [type];
    const entities = entityTypes.length
      ? filters.entities.filter((key) => entityTypes.includes(key.split(":")[0] as CheckGridSubjectType))
      : filters.entities;
    onChange({ ...filters, entityTypes, entities });
  };

  /** HQ `assigned_to` is a single board-user id. */
  const setAssignee = (value: TComboSelectedValue | null) => {
    if (value == null || value === "") {
      set("assignees", []);
      return;
    }
    const id = Number(Array.isArray(value) ? value[0] : value);
    set("assignees", Number.isFinite(id) ? [id] : []);
  };

  const toStrings = (value: TComboSelectedValue | null) => (Array.isArray(value) ? value.map(String) : []);

  return (
    <div className="shrink-0 px-3 pt-2 pb-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            variant="background"
            inputSize="xs"
            value={filters.query}
            onChange={(event) => set("query", event.target.value)}
            placeholder="Search checklists"
            aria-label="Search checklists"
            className="h-9 py-0 pr-8 pl-9 text-[13px] md:text-[13px]"
          />
          {filters.query && (
            <button
              type="button"
              onClick={() => set("query", "")}
              aria-label="Clear search"
              className="absolute top-1/2 right-1.5 flex size-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
        </div>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={cn("relative shrink-0", active > 0 && "border-primary/50 bg-primary/10 text-foreground hover:bg-primary/15")}
              aria-label={active > 0 ? `Filters, ${active} active` : "Filters"}
            >
              <SlidersHorizontal className="size-4" aria-hidden />
              {active > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-semibold text-primary-foreground tabular-nums">
                  {active}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[360px] p-0">
            <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">Filters</h2>
              <Button type="button" variant="link" className="h-auto p-0 text-xs font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:opacity-50" disabled={active === 0} onClick={() => onChange(EMPTY_FILTERS)}>
                Clear all
              </Button>
            </div>

            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-4 py-4">
              <FilterField label="Assigned to" icon={UserRound}>
                <Combobox
                  size="sm"
                  clearable
                  options={assigneeOptions}
                  value={filters.assignees[0] ?? null}
                  onValueChange={setAssignee}
                  placeholder="Anyone"
                  searchPlaceholder="Search people"
                  emptyMessage="No people found."
                  buttonSelectedClassName="bg-muted/40 text-[13px] hover:bg-muted"
                />
              </FilterField>

              <FilterField label="Entity" icon={Layers}>
                <div className="flex flex-wrap gap-2">
                  {ENTITY_TYPE_OPTIONS.map(({ value, label, icon: Icon }) => (
                    <FilterChip
                      key={value}
                      selected={filters.entityTypes.includes(value)}
                      onClick={() => selectEntityType(value)}
                      leading={<Icon className="size-3.5" aria-hidden />}
                    >
                      {label}
                    </FilterChip>
                  ))}
                </div>
                <Combobox
                  multiSelect
                  size="sm"
                  options={entityOptions}
                  value={filters.entities}
                  onValueChange={(value) => set("entities", toStrings(value))}
                  placeholder="Any entity"
                  searchPlaceholder="Search entities"
                  emptyMessage="No entities found."
                  buttonSelectedClassName="bg-muted/40 text-[13px] hover:bg-muted"
                />
              </FilterField>

              <FilterField label="Repeats" icon={Repeat}>
                <div className="flex flex-wrap gap-2">
                  {RECURRENCE_OPTIONS.map((value) => (
                    <FilterChip key={value} selected={filters.recurrence.includes(value)} onClick={() => set("recurrence", toggle(filters.recurrence, value))}>
                      {recurrenceLabel(value)}
                    </FilterChip>
                  ))}
                </div>
              </FilterField>

              <FilterField label="Created in the last" icon={CalendarClock}>
                <div className="flex flex-wrap gap-2">
                  {CREATED_OPTIONS.map(({ value, label }) => (
                    <FilterChip key={value} selected={filters.created === value} onClick={() => set("created", filters.created === value ? "any" : value)}>
                      {label}
                    </FilterChip>
                  ))}
                </div>
              </FilterField>
            </div>

            <p className="border-t border-border/60 bg-muted/30 px-4 py-3 text-[12.5px] text-foreground/70">
              Showing <span className="font-semibold text-foreground tabular-nums">{matchCount}</span> of <span className="tabular-nums">{total}</span> checklists
            </p>
          </PopoverContent>
        </Popover>
      </div>

      {active > 0 && (
        <p className="mt-2.5 flex items-center justify-between px-1 text-[12px] text-muted-foreground">
          <span>
            <span className="text-foreground tabular-nums">{matchCount}</span> of <span className="tabular-nums">{total}</span> checklists
          </span>
          <button type="button" onClick={() => onChange(EMPTY_FILTERS)} className="rounded text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Clear
          </button>
        </p>
      )}
    </div>
  );
}
